import { FeatureMiddleware } from '@cast/api-client';
import {
  demoClusterStorageSummary,
  demoPersistentVolumeDetails,
  demoPersistentVolumesList,
  demoPersistentVolumesSummary,
  demoRootDiskMetrics,
  demoStorageActions,
  demoStorageMetrics,
  demoStorageSummary,
} from '@cast/fixtures';

import {
  getIsClusterDemoMode,
  getIsOpDemoMode,
} from '@cast/infrastructure-demo';

import { isSimulatedDemoClusterRequest, opDemoLoader } from '../utils';

export const stop: FeatureMiddleware<'stop'> = {
  getNodeStorageSummary: async ({ args, next, resolve, feature, endpoint }) => {
    if (getIsOpDemoMode()) {
      const fixture = await opDemoLoader.loadFixture({
        feature,
        endpoint,
        endpointParameters: args,
      });
      if (fixture) {
        return resolve(fixture);
      }
    }

    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoStorageSummary();
      return resolve(fixture);
    }

    return next();
  },
  getClusterStorageSummary: async ({
    args,
    next,
    resolve,
    feature,
    endpoint,
  }) => {
    if (getIsOpDemoMode()) {
      const fixture = await opDemoLoader.loadFixture({
        feature,
        endpoint,
        endpointParameters: args,
      });
      if (fixture) {
        return resolve(fixture);
      }
    }

    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterStorageSummary();
      return resolve(fixture);
    }

    return next();
  },
  listStorageActions: async ({ args, next, resolve, feature, endpoint }) => {
    if (getIsOpDemoMode()) {
      const fixture = await opDemoLoader.loadFixture({
        feature,
        endpoint,
        endpointParameters: args,
      });
      if (fixture) {
        return resolve(fixture);
      }
    }

    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoStorageActions();
      return resolve(fixture);
    }

    return next();
  },
  getNodeStorageMetrics: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture =
        args[0]?.storageType === 'root'
          ? await demoRootDiskMetrics()
          : await demoStorageMetrics();
      return resolve(fixture);
    }
    return next();
  },
  getCloudVolumeMetrics: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoStorageMetrics();
      // Cloud volume metrics uses a subset of storage metrics
      return resolve({
        series:
          fixture.series?.map((s) => ({
            bucketTime: s.bucketTime,
            iops: s.iops,
            provisionedIops: s.provisionedIops,
            throughputBytes: s.throughputBytes,
            provisionedThroughputBytes: s.provisionedThroughputBytes,
          })) ?? [],
        summary: {
          iops: fixture.summary?.iops,
          provisionedIops: fixture.summary?.provisionedIops,
          throughputBytes: fixture.summary?.throughputBytes,
          provisionedThroughputBytes:
            fixture.summary?.provisionedThroughputBytes,
        },
      });
    }
    return next();
  },
  listPersistentVolumes: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoPersistentVolumesList();
      return resolve(fixture);
    }
    return next();
  },
  getPersistentVolumesSummary: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoPersistentVolumesSummary();
      return resolve(fixture);
    }
    return next();
  },
  getPersistentVolumeDetails: async ({ args, next, resolve }) => {
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoPersistentVolumeDetails();
      return resolve(fixture);
    }
    return next();
  },
};
